import { useCallback, useEffect, useRef, useState } from 'react'
import type { EmotionEngine, EmotionResult } from '@/lib/emotion'
import { toExpressions, type Expressions } from './constants'

export type LivePhase = 'idle' | 'starting' | 'live' | 'denied' | 'nocam' | 'unsupported'

export type LiveSample = { t: number; e: Expressions | null }

/** Same bundle the landing demo ships (manage.py export_emotion_web_bundle). */
const BUNDLE_URL = `${import.meta.env.BASE_URL}models/emotion-web-v1.bin`
/** Seconds of history kept for the stream chart. */
export const HISTORY_SECONDS = 15

/**
 * Webcam + in-browser emotion engine. Frames never leave the device: the
 * worker in src/lib/emotion runs the backend's Haar + CNN pipeline locally.
 * `onResult` fires per analysed frame (~8/s) so callers can update the DOM
 * without a React render; `latest` is the throttled state for the readout.
 */
export function useLiveEmotions(onResult?: (r: EmotionResult, video: HTMLVideoElement) => void) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const engineRef = useRef<EmotionEngine | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const stopLoopRef = useRef<(() => void) | null>(null)
  const historyRef = useRef<LiveSample[]>([])
  const onResultRef = useRef(onResult)
  onResultRef.current = onResult

  const [phase, setPhase] = useState<LivePhase>('idle')
  const [latest, setLatest] = useState<{ face: boolean; e: Expressions | null }>({ face: false, e: null })

  const release = useCallback(() => {
    stopLoopRef.current?.()
    stopLoopRef.current = null
    streamRef.current?.getTracks().forEach((tr) => tr.stop())
    streamRef.current = null
    if (videoRef.current) videoRef.current.srcObject = null
  }, [])

  const start = useCallback(async () => {
    setPhase('starting')
    try {
      const [engine, stream] = await Promise.all([
        engineRef.current
          ? Promise.resolve(engineRef.current)
          : import('@/lib/emotion').then((m) => m.createEmotionEngine({ bundleUrl: BUNDLE_URL })),
        navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        }),
      ])
      engineRef.current = engine
      streamRef.current = stream
    } catch (err) {
      console.error('[FaceWise demo]', err)
      const name = (err as { name?: string } | null)?.name
      release()
      if (name === 'NotAllowedError' || name === 'SecurityError') setPhase('denied')
      else if (name === 'NotFoundError' || name === 'OverconstrainedError') setPhase('nocam')
      else setPhase('unsupported')
      return
    }

    const video = videoRef.current
    if (!video || !streamRef.current) return
    video.srcObject = streamRef.current
    await video.play().catch(() => {})
    historyRef.current = []
    setLatest({ face: false, e: null })
    setPhase('live')

    const { runEmotionLoop } = await import('@/lib/emotion')
    stopLoopRef.current = runEmotionLoop(
      engineRef.current!,
      video,
      (r) => {
        const e = r.face_detected ? toExpressions(r.emotions) : null
        const now = performance.now()
        const h = historyRef.current
        h.push({ t: now, e })
        while (h.length && now - h[0].t > (HISTORY_SECONDS + 1) * 1000) h.shift()
        setLatest({ face: r.face_detected, e })
        onResultRef.current?.(r, video)
      },
      { maxFps: 8, smoothing: 0.6 },
    )
  }, [release])

  const stop = useCallback(() => {
    release()
    setPhase('idle')
    setLatest({ face: false, e: null })
  }, [release])

  // Background tab: pause the video, the loop skips paused frames.
  useEffect(() => {
    const onVis = () => {
      const v = videoRef.current
      if (!v || !streamRef.current) return
      if (document.hidden) v.pause()
      else v.play().catch(() => {})
    }
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [])

  useEffect(
    () => () => {
      release()
      engineRef.current?.dispose()
      engineRef.current = null
    },
    [release],
  )

  return { videoRef, historyRef, phase, latest, start, stop }
}
